/**
 * Pure helpers for the My Rides list (pages/RideHistory.js), kept out of the
 * component so the rules can be unit-tested.
 *
 * The list has two sources: the `live` newest rides (AppContext, real time) and
 * the `older` pages fetched once from Firestore (never updated afterwards).
 */
import { toMillis } from './rideData.js';
import { tidyPlace } from './intertownDisplay.js';

/**
 * Both sources merged by id, newest first. When the same ride is in both, `live`
 * wins (it is real time) — except a rating, which is never lost: the older copy
 * may already carry a rating the live copy has not received yet.
 */
export const mergeRideSources = (older = [], live = []) => {
  const byId = new Map();
  older.forEach((ride) => { if (ride?.id) byId.set(ride.id, ride); });
  live.forEach((ride) => {
    if (!ride?.id) return;
    const previous = byId.get(ride.id);
    byId.set(ride.id, previous && !ride.rating && previous.rating ? { ...ride, rating: previous.rating } : ride);
  });
  return [...byId.values()].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
};

// Intertown trips live in their own collection (`intertownRequests`), so they never arrive
// with the rider's local rides. Only finished ones are listed: a trip in progress is on the
// Intertown tab.
const FINISHED_INTERTOWN = new Set(['completed', 'cancelled']);

/** A finished Intertown request in the shape a My Rides card reads (`kind` marks it). */
export const intertownRideItem = (request) => ({
  id: `intertown:${request.id}`,
  requestId: request.id,
  kind: 'intertown',
  status: request.status,
  tripType: request.tripType === 'return' ? 'return' : 'one_way',
  createdAt: request.createdAt,
  // totalFare is the base fare plus any waiting charge on a return trip.
  fare: Math.max(0, Math.round(Number(request.totalFare) || Number(request.baseFare) || 0)),
  pickup: tidyPlace(request.pickup),
  destination: tidyPlace(request.destination),
});

/**
 * The My Rides list: the rider's local rides plus their finished Intertown trips, newest first.
 *
 * Local rides arrive in pages; every Intertown trip is already in memory. While older local
 * rides are still to be loaded (`hasMore`), an Intertown trip older than the oldest loaded ride
 * waits for them, so "Load older rides" only ever adds above what the rider already sees.
 */
export const withIntertownTrips = (rides = [], intertownRequests = [], { hasMore = false } = {}) => {
  const oldestLoaded = rides.reduce((oldest, ride) => Math.min(oldest, toMillis(ride?.createdAt) || Infinity), Infinity);
  const waitForOlderRides = hasMore && Number.isFinite(oldestLoaded);
  const trips = (intertownRequests || [])
    .filter((request) => request?.id && FINISHED_INTERTOWN.has(request.status))
    .filter((request) => !waitForOlderRides || toMillis(request.createdAt) >= oldestLoaded)
    .map(intertownRideItem);
  if (!trips.length) return rides;
  return [...rides, ...trips].sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
};

/** The list with one ride's rating set (the older pages are a snapshot, so patch it locally). */
export const withRating = (rides = [], rideId, rating) => (
  rides.map((ride) => (ride.id === rideId ? { ...ride, rating } : ride))
);

/**
 * Whether more rides may exist after a (re)fetch of the first page. If the rider had
 * already loaded further than the first page, that deeper answer is still the truth.
 */
export const hasMoreAfterRefresh = ({ cachedCount, freshCount, cachedHasMore, freshHasMore }) => (
  cachedCount > freshCount ? Boolean(cachedHasMore) : Boolean(freshHasMore)
);

/** i18n key for an empty filter tab (not "No rides yet" — the rider has rides). */
export const filterEmptyKey = (filter) => (
  filter === 'cancelled' ? 'history.noCancelled' : 'history.noCompleted'
);

/** A ride from another calendar year needs its year shown, or it reads like this year. */
export const needsYear = (date, now = new Date()) => date.getFullYear() !== now.getFullYear();

// What the Rides tab remembers while the app stays open, so opening a receipt and coming
// back (or switching tabs) does not throw away the filter and the pages already loaded.
// Scoped by user id, so it can never show one rider another's rides.
let session = null;

/** Remember the list state for `uid`. Keeps a pending scroll position, if any. */
export const saveHistorySession = (uid, { filter, olderRides, hasMore }) => {
  if (!uid) return;
  session = {
    uid,
    filter,
    olderRides,
    hasMore: Boolean(hasMore),
    scrollY: session?.uid === uid ? session.scrollY : null,
  };
};

/** Note where the list was scrolled when a ride was opened, to restore on return. */
export const rememberHistoryScroll = (uid, scrollY) => {
  if (!uid || session?.uid !== uid) return;
  session = { ...session, scrollY: Number.isFinite(scrollY) ? scrollY : null };
};

/** What was remembered for `uid` (or null). Does not change anything. */
export const readHistorySession = (uid) => (uid && session?.uid === uid ? session : null);

/** The scroll position has been restored (or is no longer wanted). */
export const clearHistoryScroll = (uid) => {
  if (uid && session?.uid === uid) session = { ...session, scrollY: null };
};

/** Forget everything (tests, sign-out). */
export const clearHistorySession = () => { session = null; };
